import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Upload, Trash, Plus, PencilSimple, X } from "@phosphor-icons/react";
import { listBrands, createBrand, updateBrand, deleteBrand, uploadImage, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

const POSITIONS = [
  "top-left", "top-center", "top-right",
  "center-left", "center", "center-right",
  "bottom-left", "bottom-center", "bottom-right",
];
const SIZES = ["sm", "md", "lg"];

const EMPTY_BRAND = {
  name: "Untitled brand", handle: "", logo_url: "",
  brand_color: "#244A36", show_on_slides: true,
  logo_position: "top-right", logo_size: "md",
  handle_position: "bottom-left",
  website: "", show_website: false,
  website_position: "bottom-center", website_size: "sm",
};

export default function BrandSettings({ open, onOpenChange, onSaved }) {
  const { lang } = useI18n();
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null); // brand being edited or null = list view
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listBrands();
      setBrands(list);
    } catch {
      setBrands([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { refresh(); setEditing(null); } }, [open]);

  const startNew = () => setEditing({ ...EMPTY_BRAND });
  const startEdit = (b) => setEditing({ ...b });

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setEditing((b) => ({ ...b, logo_url: res.url }));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    try {
      let saved;
      if (editing.id) saved = await updateBrand(editing.id, editing);
      else saved = await createBrand(editing);
      toast.success(lang === "el" ? "Αποθηκεύτηκε" : "Saved");
      await refresh();
      setEditing(null);
      onSaved?.(saved);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && String(detail).toLowerCase().includes("limit")) {
        toast.error(lang === "el"
          ? "Φτάσατε το όριο 5 brands. Διαγράψτε ένα για να προσθέσετε νέο."
          : "You've reached the 5-brand limit. Delete one to add a new one.");
      } else {
        toast.error(detail || (lang === "el" ? "Αποτυχία αποθήκευσης" : "Save failed"));
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === "el" ? "Διαγραφή brand;" : "Delete brand?")) return;
    try {
      await deleteBrand(id);
      await refresh();
      toast.success(lang === "el" ? "Διαγράφηκε" : "Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const set = (patch) => setEditing((b) => ({ ...b, ...patch }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-line rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto custom-scrollbar" data-testid="brand-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {editing ? (editing.id ? (lang === "el" ? "Επεξεργασία brand" : "Edit brand") : (lang === "el" ? "Νέο brand" : "New brand")) : (lang === "el" ? "Brand presets" : "Brand presets")}
          </DialogTitle>
          <DialogDescription className="text-sm text-ink-soft">
            {editing
              ? (lang === "el" ? "Αποθηκεύεται ξεχωριστά ανά πελάτη" : "Saved per client")
              : (lang === "el"
                  ? `Μέχρι 5 brands ανά λογαριασμό · ${brands.length}/5 σε χρήση`
                  : `Up to 5 brands per account · ${brands.length}/5 used`)}
          </DialogDescription>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-3 mt-2">
            {loading ? (
              <div className="text-ink-soft text-sm py-6 text-center">Loading…</div>
            ) : brands.length === 0 ? (
              <div className="text-ink-soft text-sm py-8 text-center border border-dashed border-line rounded-xl">
                {lang === "el" ? "Κανένα brand ακόμη." : "No brands yet."}
              </div>
            ) : (
              <div className="space-y-2" data-testid="brand-list">
                {brands.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 p-3 border border-line rounded-xl hover:border-forest/40 transition" data-testid={`brand-item-${b.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-cream-deep flex items-center justify-center overflow-hidden border border-line">
                      {b.logo_url ? <img src={fileUrl(b.logo_url)} alt="" className="w-full h-full object-contain" /> : <span className="text-[10px] text-ink-soft uppercase">NO LOGO</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink text-sm truncate">{b.name || b.handle || "Untitled"}</div>
                      <div className="text-xs text-ink-soft truncate">{b.handle || b.website || "—"}</div>
                    </div>
                    <button onClick={() => startEdit(b)} className="p-2 text-ink-soft hover:text-forest" data-testid={`brand-edit-${b.id}`}><PencilSimple size={14} weight="duotone" /></button>
                    <button onClick={() => remove(b.id)} className="p-2 text-ink-soft hover:text-destructive" data-testid={`brand-delete-${b.id}`}><Trash size={14} weight="duotone" /></button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={startNew}
              disabled={brands.length >= 5}
              className="w-full py-3 rounded-lg border border-dashed border-line text-ink-soft hover:text-forest hover:border-forest transition text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink-soft disabled:hover:border-line"
              data-testid="brand-new"
              title={brands.length >= 5 ? (lang === "el" ? "Όριο 5 brands" : "5-brand limit reached") : ""}
            >
              <Plus size={14} weight="bold" />
              {brands.length >= 5
                ? (lang === "el" ? "Όριο 5 brands · διαγράψτε ένα" : "5-brand limit · delete one to add")
                : (lang === "el" ? "Νέο brand" : "New brand")}
            </button>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <Field label={lang === "el" ? "Όνομα brand" : "Brand name"}>
              <input value={editing.name || ""} onChange={(e) => set({ name: e.target.value })} className="cs-input" data-testid="brand-name" placeholder={lang === "el" ? "π.χ. Πελάτης Α" : "e.g. Client A"} />
            </Field>
            <Field label="Handle">
              <input value={editing.handle || ""} onChange={(e) => set({ handle: e.target.value })} className="cs-input" placeholder="@yourbrand" data-testid="brand-handle" />
              {editing.handle && (
                <div className="mt-2">
                  <div className="kicker-label mb-2">{lang === "el" ? "Θέση handle" : "Handle position"}</div>
                  <div className="grid grid-cols-3 gap-1.5" data-testid="handle-position-grid">
                    {POSITIONS.map((p) => (
                      <button key={p} onClick={() => set({ handle_position: p })}
                        className={`h-8 rounded-md border text-[10px] transition ${editing.handle_position === p ? "bg-forest text-cream border-forest" : "border-line text-ink-soft hover:border-forest"}`}
                        data-testid={`handle-pos-${p}`}>
                        {p.replace("-", "·")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Field>

            <Field label="Logo">
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }} data-testid="brand-logo-input" />
              {editing.logo_url ? (
                <div className="flex items-center gap-3">
                  <img src={fileUrl(editing.logo_url)} alt="" className="w-14 h-14 rounded-lg object-contain border border-line bg-cream-deep" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs px-3 py-2 rounded-full border border-line hover:border-forest hover:text-forest" data-testid="brand-logo-replace">{uploading ? "…" : "Replace"}</button>
                  <button onClick={() => set({ logo_url: "" })} className="text-xs px-3 py-2 rounded-full border border-line hover:border-destructive hover:text-destructive" data-testid="brand-logo-remove"><Trash size={12} /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full py-4 rounded-lg border border-dashed border-line text-ink-soft hover:text-forest hover:border-forest text-xs flex items-center justify-center gap-2" data-testid="brand-logo-upload">
                  <Upload size={14} weight="duotone" /> {uploading ? "Uploading…" : "Upload logo"}
                </button>
              )}
            </Field>

            {editing.logo_url && (
              <>
                <Field label="Logo position">
                  <div className="grid grid-cols-3 gap-1.5" data-testid="logo-position-grid">
                    {POSITIONS.map((p) => (
                      <button key={p} onClick={() => set({ logo_position: p })}
                        className={`h-9 rounded-md border text-[10px] transition ${editing.logo_position === p ? "bg-forest text-cream border-forest" : "border-line text-ink-soft hover:border-forest"}`}
                        data-testid={`logo-pos-${p}`}>
                        {p.replace("-", "·")}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Logo size">
                  <div className="flex gap-2" data-testid="logo-size-group">
                    {SIZES.map((s) => (
                      <button key={s} onClick={() => set({ logo_size: s })}
                        className={`chip ${editing.logo_size === s ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs uppercase`}
                        data-testid={`logo-size-${s}`}>{s}</button>
                    ))}
                  </div>
                </Field>
              </>
            )}

            <Field label={`Website ${lang === "el" ? "(προαιρετικό)" : "(optional)"}`}>
              <input value={editing.website || ""} onChange={(e) => set({ website: e.target.value })} className="cs-input" placeholder="www.yourbrand.com" data-testid="brand-website" />
              <label className="flex items-center justify-between mt-2 p-3 rounded-lg border border-line cursor-pointer" data-testid="brand-show-website">
                <div className="text-xs text-ink-soft">{lang === "el" ? "Εμφάνιση στο slide" : "Show on slides"}</div>
                <input type="checkbox" checked={!!editing.show_website} onChange={(e) => set({ show_website: e.target.checked })} className="w-5 h-5 accent-[#244A36]" />
              </label>
              {editing.show_website && (
                <>
                  <div className="mt-3">
                    <div className="kicker-label mb-2">{lang === "el" ? "Θέση website" : "Website position"}</div>
                    <div className="grid grid-cols-3 gap-1.5" data-testid="website-position-grid">
                      {POSITIONS.map((p) => (
                        <button key={p} onClick={() => set({ website_position: p })}
                          className={`h-8 rounded-md border text-[10px] transition ${editing.website_position === p ? "bg-forest text-cream border-forest" : "border-line text-ink-soft hover:border-forest"}`}
                          data-testid={`website-pos-${p}`}>
                          {p.replace("-", "·")}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="kicker-label mb-2">{lang === "el" ? "Μέγεθος website" : "Website size"}</div>
                    <div className="flex gap-2" data-testid="website-size-group">
                      {SIZES.map((s) => (
                        <button key={s} onClick={() => set({ website_size: s })}
                          className={`chip ${editing.website_size === s ? "is-active" : ""} px-3 py-1.5 rounded-full text-xs uppercase`}
                          data-testid={`website-size-${s}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </Field>

            <Field label="Brand color">
              <div className="flex items-center gap-3">
                <input type="color" value={editing.brand_color || "#244A36"} onChange={(e) => set({ brand_color: e.target.value })} className="w-10 h-10 rounded-full border border-line cursor-pointer" data-testid="brand-color" />
                <input value={editing.brand_color || ""} onChange={(e) => set({ brand_color: e.target.value })} className="cs-input max-w-[200px]" />
              </div>
            </Field>

            <label className="flex items-center justify-between p-3 rounded-lg border border-line cursor-pointer" data-testid="brand-toggle">
              <div>
                <div className="text-sm font-medium text-ink">{lang === "el" ? "Εμφάνιση λογοτύπου & handle" : "Show logo & handle"}</div>
                <div className="text-xs text-ink-soft">{lang === "el" ? "Σε όλα τα slides" : "On every slide"}</div>
              </div>
              <input type="checkbox" checked={!!editing.show_on_slides} onChange={(e) => set({ show_on_slides: e.target.checked })} className="w-5 h-5 accent-[#244A36]" />
            </label>

            <div className="flex justify-between gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-sm text-ink-soft hover:text-ink" data-testid="brand-cancel">
                <X size={14} className="inline mr-1" /> {lang === "el" ? "Πίσω" : "Back"}
              </button>
              <button onClick={save} className="px-6 py-2.5 rounded-full text-sm bg-forest text-cream hover:bg-forest-dark font-medium" data-testid="brand-save">
                {lang === "el" ? "Αποθήκευση" : "Save"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-2">
      <span className="kicker-label block">{label}</span>
      {children}
    </label>
  );
}
